import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierReport, getCustomerAnalytics } from '../../../../redux/services/reportsService';
import { formatMoney } from '@dill/dill-shared/dist/src';
import { AppButton } from '../../../general/AppButton/AppButton';
import { DillSelect } from '../../../general/Dill';
import SupplierTotalPaymentsChart from '../../suppliers/SupplierReporting/PaymentsReports/SupplierTotalPaymentsChart/SupplierTotalPaymentsChart';
import SupplierTopPayingCustomersChart from '../../suppliers/SupplierReporting/PaymentsReports/SupplierTopPayingCustomersChart/SupplierTopPayingCustomersChart';
import DSOMetricsCard from '../../suppliers/SupplierReporting/CustomerAnalytics/DSOMetricsCard';
import CashFlowTrendChart from '../../suppliers/SupplierReporting/CustomerAnalytics/CashFlowTrendChart';
import SlowestPayersTable from '../../suppliers/SupplierReporting/CustomerAnalytics/SlowestPayersTable';
import PaymentMethodComparisonChart from '../../suppliers/SupplierReporting/CustomerAnalytics/PaymentMethodComparisonChart';
import PaymentPatternsChart from '../../suppliers/SupplierReporting/CustomerAnalytics/PaymentPatternsChart';
import AdoptionMetricsCards from '../../suppliers/SupplierReporting/CustomerAnalytics/AdoptionMetricsCards';
import EarlyPayersCard from '../../suppliers/SupplierReporting/CustomerAnalytics/EarlyPayersCard';
import CreditManagerPerformanceTable from '../../suppliers/SupplierReporting/CustomerAnalytics/CreditManagerPerformanceTable';
import { APP_ENVIRONMENT } from '../../../../utils/constants';

const SupplierPaymentsDashboard = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierAnnualReport, customerAnalyticsReport, loadingList } = useAppSelector(
    (state) => state.reports
  );
  const [selectedYear, setSelectedYear] = useState(`${new Date().getFullYear()}`);

  const yearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(`${currentYear - i}`);
    }
    return years;
  }, []);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierReport({ supplierId: currentSupplier.id, year: selectedYear }));
      dispatch(getCustomerAnalytics({ supplierId: currentSupplier.id, year: selectedYear }));
    }
  }, [currentSupplier?.id, selectedYear]);

  const kpiData = useMemo(() => {
    if (!supplierAnnualReport) {
      return {
        totalRevenue: 0,
        totalTransactions: 0,
        avgTransactionSize: 0,
        topCustomer: null,
        cardPaymentsTotal: 0,
        bankPaymentsTotal: 0,
        mostActiveMonth: null,
        transactionGrowth: 0,
      };
    }

    const { yearSummary, yearMonthlySummary } = supplierAnnualReport;

    const cardPaymentsTotal = yearMonthlySummary.reduce(
      (sum, month) => sum + (month.paymentTypes.CARD?.totalAmount || 0),
      0
    );
    const bankPaymentsTotal = yearMonthlySummary.reduce(
      (sum, month) => sum + (month.paymentTypes.BANK?.totalAmount || 0),
      0
    );
    const totalRevenue = cardPaymentsTotal + bankPaymentsTotal;

    const totalTransactions = yearMonthlySummary.reduce(
      (sum, month) =>
        sum +
        (month.paymentTypes.CARD?.numberOfTransactions || 0) +
        (month.paymentTypes.BANK?.numberOfTransactions || 0),
      0
    );

    const avgTransactionSize = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    const mostActiveMonth = yearMonthlySummary.reduce((max, month) => {
      const monthTotal =
        (month.paymentTypes.CARD?.totalAmount || 0) + (month.paymentTypes.BANK?.totalAmount || 0);
      const maxTotal =
        (max.paymentTypes.CARD?.totalAmount || 0) + (max.paymentTypes.BANK?.totalAmount || 0);
      return monthTotal > maxTotal ? month : max;
    }, yearMonthlySummary[0]);

    return {
      totalRevenue,
      totalTransactions,
      avgTransactionSize,
      topCustomer: yearSummary.topCustomersOfYear[0] || null,
      cardPaymentsTotal,
      bankPaymentsTotal,
      mostActiveMonth: mostActiveMonth?.month || null,
      transactionGrowth: 0,
    };
  }, [supplierAnnualReport]);

  const isLoading =
    loadingList.includes('getCustomerAnalytics') || loadingList.includes('getSupplierReport');

  return (
    <div className="flex flex-col w-full h-full">
      <div className="sticky top-0 bg-white z-30 border-b border-gray-200">
        <div className="px-4">
          <div className="flex items-center py-4">
            <div className="min-w-[120px] sm:min-w-[140px]">
              <DillSelect
                value={selectedYear}
                options={yearsList.map((year) => ({ label: year, value: year }))}
                onChange={(value) => {
                  if (yearsList.includes(value)) {
                    setSelectedYear(value);
                  }
                }}
              />
            </div>
            {/* Non-blocking: the dashboard stays visible and interactive while
                either request is in flight, so this only annotates that the
                figures on screen are still being refreshed. */}
            {isLoading && (
              <div className="flex items-center gap-2 ml-3 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                <span>Updating…</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full h-full p-4 pb-24 overflow-scroll">
        <div
          className={`flex flex-col gap-4 transition-opacity duration-200 ${
            isLoading ? 'opacity-60' : 'opacity-100'
          }`}>
          {/* KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Revenue */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {formatMoney(kpiData.totalRevenue, 2)}
              </p>
              <p className="text-sm text-gray-500">For {selectedYear}</p>
            </div>

            {/* Total Transactions */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Total Transactions</h3>
                <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {kpiData.totalTransactions.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Payment Transactions</p>
            </div>

            {/* Average Transaction Size */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Avg Transaction</h3>
                <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {formatMoney(kpiData.avgTransactionSize, 2)}
              </p>
              <p className="text-sm text-gray-500">Per Transaction</p>
            </div>

            {/* Top Customer */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Top Customer</h3>
                <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1 truncate">
                {kpiData.topCustomer?.customer?.name || 'No data'}
              </p>
              <p className="text-sm text-gray-500">
                {kpiData.topCustomer ? formatMoney(kpiData.topCustomer.totalAmount, 2) : '—'}
              </p>
            </div>
          </div>

          {/* DSO & Adoption Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DSOMetricsCard dsoMetrics={customerAnalyticsReport?.dsoMetrics} />
            <AdoptionMetricsCards adoptionMetrics={customerAnalyticsReport} />
          </div>

          {/* Cash Flow Trend - Full Width */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
            <CashFlowTrendChart data={customerAnalyticsReport?.cashFlowTrend || []} />
          </div>

          {/* Total Payments Initiated on Dill - Full Width */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
            <SupplierTotalPaymentsChart />
          </div>

          {/* Payment Patterns - Full Width */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
            <PaymentPatternsChart data={customerAnalyticsReport?.paymentPatterns || []} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow duration-300">
              <SupplierTopPayingCustomersChart />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
              <PaymentMethodComparisonChart
                data={customerAnalyticsReport?.paymentMethodComparison || []}
              />
            </div>
          </div>

          {/* Slowest Payers & Early Payers */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                Top 10 Slowest Payers
              </h3>
              <p className="text-sm text-gray-500 mb-4">Customers with longest payment cycles</p>
              <SlowestPayersTable data={customerAnalyticsReport?.slowestPayers || []} />
            </div>
            <EarlyPayersCard customers={customerAnalyticsReport?.earlyPayers || []} />
          </div>

          {/* Performance by Credit Manager - Full Width */}
          <CreditManagerPerformanceTable />
        </div>

        {/* Floating Export Button */}
        {APP_ENVIRONMENT !== 'production' && (
          <AppButton
            text="Export"
            type="PRIMARY"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
            buttonStyles={{
              position: 'fixed',
              bottom: '1.5rem',
              right: '1.5rem',
              borderRadius: '9999px',
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              zIndex: 50,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SupplierPaymentsDashboard;
